import {
  Dropdown,
  Field,
  makeStyles,
  Option,
  Switch,
  tokens,
} from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import {
  ChoiceInvalidValueBehavior,
  ChoiceOrientation,
  ChoiceValueSort,
  MultiSelectChoiceEdit,
  Section,
  SectionColumn,
} from '@powerportalspro/react-fluent';
import { useMemo, useState } from 'react';

// Localization namespace — reuses the Blazor demo's
// `MultiSelectChoiceEditPropertiesDemo` keys so both stacks read from the
// same JSON.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Editors.MultiSelectChoiceEdit.MultiSelectChoiceEditPropertiesDemo';

const ORIENTATION_VALUES = [
  ChoiceOrientation.Vertical,
  ChoiceOrientation.Horizontal,
] as const;
const SORT_VALUES = [
  ChoiceValueSort.Default,
  ChoiceValueSort.DisplayName,
  ChoiceValueSort.DisplayNameDescending,
  ChoiceValueSort.Value,
  ChoiceValueSort.ValueDescending,
] as const;
const INVALID_BEHAVIOR_VALUES = [
  ChoiceInvalidValueBehavior.Disable,
  ChoiceInvalidValueBehavior.Remove,
] as const;

const INVALID_CHOICE_VALUES = [100000002, 100000004] as const;

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  inlineField: {
    flexShrink: 0,
    gridTemplateColumns: 'auto auto',
    '& label': {
      whiteSpace: 'nowrap',
    },
  },
  selectInput: {
    minWidth: '125px',
  },
});

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function MultiSelectChoiceEditPropertiesSample() {
  const styles = useStyles();
  const t = useT();
  const [readOnly, setReadOnly] = useState(false);
  const [required, setRequired] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [displayChoiceColors, setDisplayChoiceColors] = useState(true);
  const [wrapCheckboxes, setWrapCheckboxes] = useState(false);
  const [orientation, setOrientation] = useState<ChoiceOrientation>(ChoiceOrientation.Vertical);
  const [sort, setSort] = useState<ChoiceValueSort>(ChoiceValueSort.Default);
  const [invalidValueBehavior, setInvalidValueBehavior] = useState<ChoiceInvalidValueBehavior>(
    ChoiceInvalidValueBehavior.Disable,
  );

  const orientationOptions = useMemo(
    () =>
      ORIENTATION_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.orientation.${cap(value)}`),
      })),
    [t],
  );
  const sortOptions = useMemo(
    () =>
      SORT_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.sortOrder.${cap(value)}`),
      })),
    [t],
  );
  const invalidBehaviorOptions = useMemo(
    () =>
      INVALID_BEHAVIOR_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.invalidValueBehavior.${cap(value)}`),
      })),
    [t],
  );

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Switch
              label={t(`${STRINGS_BASE}.readonly`)}
              labelPosition="before"
              checked={readOnly}
              onChange={(_, d) => setReadOnly(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.required`)}
              labelPosition="before"
              checked={required}
              onChange={(_, d) => setRequired(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.disabled`)}
              labelPosition="before"
              checked={disabled}
              onChange={(_, d) => setDisabled(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.display-choice-colors`)}
              labelPosition="before"
              checked={displayChoiceColors}
              onChange={(_, d) => setDisplayChoiceColors(d.checked)}
            />
            <Field
              label={t(`${STRINGS_BASE}.sort-order`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={sortOptions.find((o) => o.value === sort)?.label ?? sort}
                selectedOptions={[sort]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setSort(d.optionValue as ChoiceValueSort);
                }}
              >
                {sortOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.invalid-value-behavior`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={
                  invalidBehaviorOptions.find((o) => o.value === invalidValueBehavior)?.label ??
                  invalidValueBehavior
                }
                selectedOptions={[invalidValueBehavior]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue)
                    setInvalidValueBehavior(d.optionValue as ChoiceInvalidValueBehavior);
                }}
              >
                {invalidBehaviorOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.radio-buttons-orientation`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={
                  orientationOptions.find((o) => o.value === orientation)?.label ?? orientation
                }
                selectedOptions={[orientation]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setOrientation(d.optionValue as ChoiceOrientation);
                }}
              >
                {orientationOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Switch
              label={t(`${STRINGS_BASE}.wrap-checkboxes`)}
              labelPosition="before"
              checked={wrapCheckboxes}
              onChange={(_, d) => setWrapCheckboxes(d.checked)}
            />
          </div>
        }
      >
        <MultiSelectChoiceEdit
          columnName="ppp_favoritecolors"
          // A definite height (not max-height) gives the vertical column-wrap a
          // stable height to wrap against — otherwise the container collapses to
          // its content and shifts as you toggle wrap / check options.
          style={{ height: 100 }}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          displayChoiceColors={displayChoiceColors}
          wrapCheckboxes={wrapCheckboxes}
          invalidChoiceValues={INVALID_CHOICE_VALUES}
          invalidValueBehavior={invalidValueBehavior}
          checkboxOrientation={orientation}
          sort={sort}
        />
      </SectionColumn>
    </Section>
  );
}
